import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { categories } from '@/mocks/categories';
import { getCategoryName, validLangs } from '@/mocks/languages';

export default function CategoryBrowser() {
  const { t } = useTranslation();
  const { lang } = useParams();
  const currentLang = lang && validLangs.includes(lang) ? lang : 'en';

  const [selected, setSelected] = useState('');
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setSelected('');
    setOpen(false);
  }, [currentLang]);

  const handleGo = (url: string) => {
    window.open(url, '_blank', 'noopener');
  };

  return (
    <section id="categories" className="w-full max-w-6xl mx-auto px-4 md:px-6 py-12 md:py-16">
      <div className="text-center mb-8">
        <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-950">
          {t('home.categories.title')}
        </h2>
        <p className="mt-2 text-foreground-600 text-sm md:text-base max-w-xl mx-auto">
          {t('home.categories.subtitle')}
        </p>
      </div>

      {/* Dropdown selector */}
      <div className="max-w-xl mx-auto mb-10">
        <label className="block text-sm text-foreground-700 mb-2 font-medium">
          {t('home.search.categoryLabel')}
        </label>
        <div className="relative">
          <button
            onClick={() => setOpen((v) => !v)}
            className="w-full flex items-center justify-between px-4 py-3 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-800 cursor-pointer hover:border-primary-300 transition-colors"
          >
            <span className={selected ? 'text-foreground-900' : 'text-foreground-500'}>
              {selected || t('home.search.selectPlaceholder')}
            </span>
            <i className="ri-arrow-down-s-line text-foreground-500"></i>
          </button>

          {open && (
            <div className="absolute z-20 left-0 right-0 mt-1 max-h-72 overflow-y-auto rounded-lg border border-background-200 bg-background-50 py-1">
              {categories.map((cat) => (
                <button
                  key={cat.key}
                  onClick={() => {
                    setSelected(getCategoryName(cat, currentLang));
                    setOpen(false);
                  }}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 cursor-pointer"
                >
                  <span className="w-7 h-7 flex items-center justify-center rounded-md bg-secondary-100 text-secondary-800">
                    <i className={cat.icon}></i>
                  </span>
                  {getCategoryName(cat, currentLang)}
                </button>
              ))}
            </div>
          )}
        </div>

        <button
          onClick={() => {
            const found = categories.find((c) => getCategoryName(c, currentLang) === selected);
            if (found) handleGo(found.url);
          }}
          disabled={!selected}
          className="mt-3 w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg bg-primary-500 text-background-50 text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed hover:bg-primary-600 transition-colors"
        >
          {t('home.search.go')}
          <i className="ri-external-link-line"></i>
        </button>
      </div>

      {/* Category card buttons with images */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
        {categories.map((cat) => (
          <a
            key={cat.key}
            href={cat.url}
            target="_blank"
            rel="noopener nofollow"
            className="group rounded-lg overflow-hidden bg-background-50 border border-background-200/70 hover:border-primary-300 transition-colors cursor-pointer"
          >
            <div className="w-full h-28 md:h-32 overflow-hidden">
              <img
                src={cat.image}
                alt={getCategoryName(cat, currentLang)}
                title={`${getCategoryName(cat, currentLang)} AllChinaBuy Spreadsheet`}
                className="w-full h-full object-cover object-top transition-transform duration-300 group-hover:scale-105"
              />
            </div>
            <div className="flex items-center justify-between px-3 py-2.5">
              <span className="text-sm font-medium text-foreground-900 text-left leading-snug">
                {getCategoryName(cat, currentLang)}
              </span>
              <i className="ri-arrow-right-up-line text-foreground-400 group-hover:text-primary-500 transition-colors"></i>
            </div>
          </a>
        ))}
      </div>
    </section>
  );
}